"use client";

import { useState } from "react";
import { services } from "@/lib/site";
import Icon from "./Icon";

export default function QuoteForm() {
  const [submitted, setSubmitted] = useState(false);

  function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setSubmitted(true);
  }

  if (submitted) {
    return (
      <div className="flex h-full flex-col items-center justify-center rounded-2xl bg-white p-10 text-center shadow-xl">
        <span className="flex h-16 w-16 items-center justify-center rounded-full bg-green-100 text-green-600">
          <Icon name="check" className="h-9 w-9" />
        </span>
        <h3 className="mt-5 text-2xl font-bold text-navy-800">
          Request Received!
        </h3>
        <p className="mt-2 max-w-sm text-slate-600">
          Thanks for reaching out to Bronco Electric. A member of our team will
          contact you shortly to discuss your project.
        </p>
        <button
          onClick={() => setSubmitted(false)}
          className="mt-6 font-semibold text-brand-600 hover:text-brand-700"
        >
          Submit another request
        </button>
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="rounded-2xl bg-white p-7 shadow-xl sm:p-8"
    >
      <h3 className="text-2xl font-bold text-navy-800">Request a Quote</h3>
      <p className="mt-1 text-sm text-slate-600">
        Tell us about your project and we’ll get back to you fast.
      </p>

      <div className="mt-6 grid gap-4 sm:grid-cols-2">
        <Field label="Full Name" name="name" required>
          <input
            type="text"
            name="name"
            required
            autoComplete="name"
            className={inputClass}
            placeholder="Jane Smith"
          />
        </Field>
        <Field label="Phone" name="phone" required>
          <input
            type="tel"
            name="phone"
            required
            autoComplete="tel"
            className={inputClass}
            placeholder="(661) 555-0123"
          />
        </Field>
        <Field label="Email" name="email" className="sm:col-span-2">
          <input
            type="email"
            name="email"
            autoComplete="email"
            className={inputClass}
            placeholder="you@email.com"
          />
        </Field>
        <Field label="Service Needed" name="service" className="sm:col-span-2">
          <select name="service" defaultValue="" className={inputClass} required>
            <option value="" disabled>
              Select a service…
            </option>
            {services.map((s) => (
              <option key={s.title} value={s.title}>
                {s.title}
              </option>
            ))}
            <option value="Other">Other / Not Sure</option>
          </select>
        </Field>
        <Field label="Project Details" name="message" className="sm:col-span-2">
          <textarea
            name="message"
            rows={4}
            className={inputClass}
            placeholder="Tell us what you need…"
          />
        </Field>
      </div>

      <button
        type="submit"
        className="mt-6 flex w-full items-center justify-center gap-2 rounded-lg bg-brand-500 px-6 py-4 text-base font-bold text-white shadow-lg shadow-brand-500/30 transition hover:bg-brand-600"
      >
        Request My Free Quote
        <Icon name="arrowRight" className="h-5 w-5" />
      </button>
      <p className="mt-3 text-center text-xs text-slate-500">
        No obligation. We typically respond within one business day.
      </p>
    </form>
  );
}

const inputClass =
  "w-full rounded-lg border border-slate-300 bg-slate-50 px-4 py-3 text-navy-800 outline-none transition placeholder:text-slate-400 focus:border-brand-500 focus:bg-white focus:ring-2 focus:ring-brand-500/30";

function Field({
  label,
  name,
  required,
  className = "",
  children,
}: {
  label: string;
  name: string;
  required?: boolean;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <label htmlFor={name} className={`block ${className}`}>
      <span className="mb-1.5 block text-sm font-semibold text-navy-700">
        {label}
        {required && <span className="text-brand-600"> *</span>}
      </span>
      {children}
    </label>
  );
}
